import React, { useState, useEffect } from "react";
import { Card, Paper, Grid, Box, Divider } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import PageLayout from "examples/LayoutContainers/PageLayout";
import AdminNavBar from "./AdminNavBar";
import axiosInstance from "API/axiosInstance";
import { API_ENDPOINTS } from "config/api";
import eventManagementImage from "assets/images/event-management.jpg";
import chaptersImage from "assets/images/chapters.png";
import alumniRightsImage from "assets/images/alumni-rights.jpg";
import alumniDetailsImage from "assets/images/alumni-details.png";
import importStudentsImage from "assets/images/import-students.png";
import sendInvitationsImage from "assets/images/send-invitations.jpg";
import studentRequestsImage from "assets/images/student-requests.jpg";
import alumniBatchWiseImage from "assets/images/alumni-batch-wise.png";
import allAlumniImage from "assets/images/all-alumni.jpg";
import uploadCurrentStudentsImage from "assets/images/upload-current-students.jpg";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import EventIcon from "@mui/icons-material/Event";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import TableChartIcon from "@mui/icons-material/TableChart";
import SchoolIcon from "@mui/icons-material/School";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import MailIcon from "@mui/icons-material/Mail";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { useNavigate } from "react-router-dom";
import Drawer from "@mui/material/Drawer";
import AdminChapterManager from "./AdminChapterManager";
import AdminEventManager from "./AdminEventManager";
import AdminAlumniRightsManager from "./AdminAlumniRightsManager";
import AdminAlumniDetailsDialog from "./AdminAlumniDetailsDialog";
import AdminImportStudentDetails from "./AdminImportStudentDetails";
import AdminAlumnusTableDialog from "./AdminAlumnusTableDialog";
import AdminAlumniBatchWiseDialog from "./AdminAlumniBatchWiseDialog";
import AdminReportsDialog from "./AdminReportsDialog";
import AdminAllAlumniDetailsDialog from "./AdminAllAlumniDetailsDialog";
import AdminUploadCurrentStudents from "./AdminUploadCurrentStudents";
import AdminSendStudentInvitations from "./AdminSendStudentInvitations";
import AdminStudentRequestsManager from "./AdminStudentRequestsManager";
import AdminImportExistingStudents from "./AdminImportExistingStudents";
import AdminUnknownUsersManager from "./AdminUnknownUsersManager";
import AdminChapterEventAnalyticsDialog from "./AdminChapterEventAnalyticsDialog";
import AdminAlumniAnalysisDialog from "./AdminAlumniAnalysisDialog";
import UnknownAlumniRegistrationDetailsDialog from "./UnknownAlumniRegistrationDetailsDialog";
import AdminGlobalChatDialog from "./AdminGlobalChatDialog";
import AdminChapterReportDialog from "./AdminChapterReportDialog";
import AdminEventReportDialog from "./AdminEventReportDialog";

// Standard card styles using MUI theme colors
const cardStyle = {
  p: 3,
  borderRadius: 2,
  textAlign: "center",
  background: "#fff",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "center",
  height: "100%",
  minHeight: 260,
  border: "1px solid #e0e0e0",
  cursor: "pointer",
  transition: "all 0.3s ease",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    borderColor: "primary.main",
  },
};

const getIconWrapperStyle = (color) => ({
  backgroundColor: (theme) => theme.palette[color]?.lighter || theme.palette.primary.lighter,
  padding: 2,
  borderRadius: "50%",
  mb: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 72,
  height: 72,
  transition: "all 0.3s ease",
});

const cardData = [
  {
    icon: <GroupWorkIcon sx={{ fontSize: 36, color: "primary.main" }} />,
    title: "Chapters",
    description: "Create, edit, and manage chapters for your alumni network.",
    button: "Manage Chapters",
    color: "primary",
    image: chaptersImage,
  },
  {
    icon: <EventIcon sx={{ fontSize: 36, color: "info.main" }} />,
    title: "Events",
    description: "Organize and oversee alumni events and gatherings.",
    button: "Manage Events",
    color: "info",
    image: eventManagementImage,
  },
  {
    icon: <VerifiedUserIcon sx={{ fontSize: 36, color: "success.main" }} />,
    title: "Alumni Rights",
    description: "Modify alumni access and rights with ease.",
    button: "Modify Rights",
    color: "success",
    image: alumniRightsImage,
  },
  {
    icon: <AccountCircleIcon sx={{ fontSize: 36, color: "warning.main" }} />,
    title: "Alumni Details",
    description: "View and manage detailed information about alumni.",
    button: "View Details",
    color: "warning",
    image: alumniDetailsImage,
  },
  {
    icon: <UploadFileIcon sx={{ fontSize: 36, color: "error.main" }} />,
    title: "Import Students",
    description: "Import student details from Excel spreadsheets.",
    button: "Import Data",
    color: "error",
    image: importStudentsImage,
  },
  {
    icon: <TableChartIcon sx={{ fontSize: 36, color: "primary.main" }} />,
    title: "All Alumni",
    description: "Browse all alumnus records in a table with search.",
    button: "View Table",
    color: "primary",
    image: allAlumniImage,
  },
  {
    icon: <SchoolIcon sx={{ fontSize: 36, color: "info.main" }} />,
    title: "Alumni Batch Wise",
    description: "View and manage alumni details organized by batch year.",
    button: "View Batches",
    color: "info",
    image: alumniBatchWiseImage,
  },
  {
    icon: <AssessmentIcon sx={{ fontSize: 36, color: "secondary.main" }} />,
    title: "Reports",
    description: "Advanced search and filter alumni data with comprehensive reporting options.",
    button: "View Reports",
    color: "secondary",
    image: allAlumniImage,
  },
  {
    icon: <AccountCircleIcon sx={{ fontSize: 36, color: "info.main" }} />,
    title: "All Alumni Details",
    description: "View, filter, and edit all alumni details with inline editing capabilities.",
    button: "Manage Details",
    color: "info",
    image: allAlumniImage,
  },
  {
    icon: <PersonAddIcon sx={{ fontSize: 36, color: "success.main" }} />,
    title: "Upload Current Students",
    description: "Upload current students and manage their portal access requests.",
    button: "Upload Students",
    color: "success",
    image: uploadCurrentStudentsImage,
  },
  {
    icon: <MailIcon sx={{ fontSize: 36, color: "warning.main" }} />,
    title: "Send Invitations",
    description: "Send invitations to current students to join the alumni portal.",
    button: "Send Invitations",
    color: "warning",
    image: sendInvitationsImage,
  },
  {
    icon: <AssignmentIcon sx={{ fontSize: 36, color: "error.main" }} />,
    title: "Student Requests",
    description: "View and approve/reject student requests for portal access.",
    button: "Manage Requests",
    color: "error",
    image: studentRequestsImage,
  },
  // Hidden: Import Existing Students
  // {
  //   icon: <UploadFileIcon sx={{ fontSize: 36, color: "primary.main" }} />,
  //   title: "Import Existing Students",
  //   description: "Import existing students and move them to the alumni portal.",
  //   button: "Import Students",
  //   color: "primary",
  //   image: uploadCurrentStudentsImage,
  // },
  {
    icon: <PersonAddIcon sx={{ fontSize: 36, color: "info.main" }} />,
    title: "Unknown Users",
    description: "Review and verify registration requests from unknown alumni.",
    button: "Manage Unknown Users",
    color: "info",
    image: studentRequestsImage,
  },
];

const AdminHome = () => {
  const navigate = useNavigate();
  const [imageErrors, setImageErrors] = useState({});
  const [alumniStats, setAlumniStats] = useState({
    totalAlumni: 0,
    registeredAlumni: 0,
    unregisteredAlumni: 0,
  });
  const [chapterEventStats, setChapterEventStats] = useState({
    totalChapters: 0,
    activeChapters: 0,
    totalEvents: 0,
    totalChapterMembers: 0,
    totalEventMembers: 0,
  });
  const [unknownAlumniStats, setUnknownAlumniStats] = useState({
    totalRequests: 0,
    pending: 0,
    approved: 0,
    cancelled: 0,
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingChapterEventStats, setLoadingChapterEventStats] = useState(true);
  const [loadingUnknownAlumniStats, setLoadingUnknownAlumniStats] = useState(true);

  const [chapterDrawerOpen, setChapterDrawerOpen] = useState(false);
  const handleOpenChapterDrawer = () => setChapterDrawerOpen(true);
  const handleCloseChapterDrawer = () => setChapterDrawerOpen(false);
  const handleOpenChapterPage = () => navigate("/admin/chapters");

  const [eventDrawerOpen, setEventDrawerOpen] = useState(false);
  const handleOpenEventDrawer = () => setEventDrawerOpen(true);
  const handleCloseEventDrawer = () => setEventDrawerOpen(false);

  const [alumniRightsDrawerOpen, setAlumniRightsDrawerOpen] = useState(false);
  const handleOpenAlumniRightsDrawer = () => setAlumniRightsDrawerOpen(true);
  const handleCloseAlumniRightsDrawer = () => setAlumniRightsDrawerOpen(false);

  const [alumniDetailsDialogOpen, setAlumniDetailsDialogOpen] = useState(false);
  const handleOpenAlumniDetailsDialog = () => setAlumniDetailsDialogOpen(true);
  const handleCloseAlumniDetailsDialog = () => setAlumniDetailsDialogOpen(false);

  const [importStudentDrawerOpen, setImportStudentDrawerOpen] = useState(false);
  const handleOpenImportStudentDrawer = () => setImportStudentDrawerOpen(true);
  const handleCloseImportStudentDrawer = () => setImportStudentDrawerOpen(false);

  // NEW: All Alumni table dialog
  const [alumnusTableDialogOpen, setAlumnusTableDialogOpen] = useState(false);
  const handleOpenAlumnusTableDialog = () => setAlumnusTableDialogOpen(true);
  const handleCloseAlumnusTableDialog = () => setAlumnusTableDialogOpen(false);

  // NEW: Alumni Batch Wise dialog
  const [alumniBatchDialogOpen, setAlumniBatchDialogOpen] = useState(false);
  const handleOpenAlumniBatchDialog = () => setAlumniBatchDialogOpen(true);
  const handleCloseAlumniBatchDialog = () => setAlumniBatchDialogOpen(false);

  // NEW: Reports dialog
  const [reportsDialogOpen, setReportsDialogOpen] = useState(false);
  const handleOpenReportsDialog = () => setReportsDialogOpen(true);
  const handleCloseReportsDialog = () => setReportsDialogOpen(false);

  // NEW: All Alumni Details dialog
  const [allAlumniDetailsDialogOpen, setAllAlumniDetailsDialogOpen] = useState(false);
  const handleOpenAllAlumniDetailsDialog = () => setAllAlumniDetailsDialogOpen(true);
  const handleCloseAllAlumniDetailsDialog = () => setAllAlumniDetailsDialogOpen(false);

  // Current Students Management
  const [uploadCurrentStudentsOpen, setUploadCurrentStudentsOpen] = useState(false);
  const handleOpenUploadCurrentStudents = () => setUploadCurrentStudentsOpen(true);
  const handleCloseUploadCurrentStudents = () => setUploadCurrentStudentsOpen(false);

  const [sendInvitationsOpen, setSendInvitationsOpen] = useState(false);
  const handleOpenSendInvitations = () => setSendInvitationsOpen(true);
  const handleCloseSendInvitations = () => setSendInvitationsOpen(false);

  const [studentRequestsOpen, setStudentRequestsOpen] = useState(false);
  const handleOpenStudentRequests = () => setStudentRequestsOpen(true);
  const handleCloseStudentRequests = () => setStudentRequestsOpen(false);

  const [importExistingStudentsOpen, setImportExistingStudentsOpen] = useState(false);
  const handleOpenImportExistingStudents = () => setImportExistingStudentsOpen(true);
  const handleCloseImportExistingStudents = () => setImportExistingStudentsOpen(false);

  const [unknownUsersOpen, setUnknownUsersOpen] = useState(false);
  const handleOpenUnknownUsers = () => setUnknownUsersOpen(true);
  const handleCloseUnknownUsers = () => setUnknownUsersOpen(false);

  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const handleOpenAnalyticsDialog = () => setAnalyticsDialogOpen(true);
  const handleCloseAnalyticsDialog = () => setAnalyticsDialogOpen(false);

  const [alumniAnalysisDialogOpen, setAlumniAnalysisDialogOpen] = useState(false);
  const handleOpenAlumniAnalysisDialog = () => setAlumniAnalysisDialogOpen(true);
  const handleCloseAlumniAnalysisDialog = () => setAlumniAnalysisDialogOpen(false);

  const [unknownAlumniDetailsDialogOpen, setUnknownAlumniDetailsDialogOpen] = useState(false);
  const handleOpenUnknownAlumniDetailsDialog = () => setUnknownAlumniDetailsDialogOpen(true);
  const handleCloseUnknownAlumniDetailsDialog = () => setUnknownAlumniDetailsDialogOpen(false);

  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const handleOpenChatDialog = () => setChatDialogOpen(true);
  const handleCloseChatDialog = () => setChatDialogOpen(false);

  const [chapterReportDialogOpen, setChapterReportDialogOpen] = useState(false);
  const [showAllChapters, setShowAllChapters] = useState(false);
  const handleOpenChapterReportDialog = (showAll = false) => {
    setShowAllChapters(showAll);
    setChapterReportDialogOpen(true);
  };
  const handleCloseChapterReportDialog = () => {
    setChapterReportDialogOpen(false);
    setShowAllChapters(false);
  };

  const [eventReportDialogOpen, setEventReportDialogOpen] = useState(false);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const handleOpenEventReportDialog = (showAll = false) => {
    setShowAllEvents(showAll);
    setEventReportDialogOpen(true);
  };
  const handleCloseEventReportDialog = () => {
    setEventReportDialogOpen(false);
    setShowAllEvents(false);
  };

  const isAnyDrawerOpen = chapterDrawerOpen || eventDrawerOpen || alumniRightsDrawerOpen;

  // Fetch alumni statistics
  useEffect(() => {
    const fetchAlumniStatistics = async () => {
      try {
        setLoadingStats(true);
        const response = await axiosInstance.get(API_ENDPOINTS.ADMIN.GET_ALUMNI_STATISTICS);
        if (response.data) {
          setAlumniStats({
            totalAlumni: response.data.totalAlumni || 0,
            registeredAlumni: response.data.registeredAlumni || 0,
            unregisteredAlumni: response.data.unregisteredAlumni || 0,
          });
        }
      } catch (error) {
        console.error("Error fetching alumni statistics:", error);
        // Keep default values (0) on error
      } finally {
        setLoadingStats(false);
      }
    };

    fetchAlumniStatistics();
  }, []);

  // Fetch chapter/event statistics
  useEffect(() => {
    const fetchChapterEventStatistics = async () => {
      try {
        setLoadingChapterEventStats(true);
        const response = await axiosInstance.get(API_ENDPOINTS.ADMIN.GET_CHAPTER_EVENT_STATISTICS);
        if (response.data) {
          setChapterEventStats({
            totalChapters: response.data.totalChapters || 0,
            activeChapters: response.data.activeChapters || 0,
            totalEvents: response.data.totalEvents || 0,
            totalChapterMembers: response.data.totalChapterMembers || 0,
            totalEventMembers: response.data.totalEventMembers || 0,
          });
        }
      } catch (error) {
        console.error("Error fetching chapter/event statistics:", error);
        // Keep default values (0) on error
      } finally {
        setLoadingChapterEventStats(false);
      }
    };

    fetchChapterEventStatistics();
  }, []);

  // Fetch unknown alumni registration statistics
  useEffect(() => {
    const fetchUnknownAlumniStatistics = async () => {
      try {
        setLoadingUnknownAlumniStats(true);
        const response = await axiosInstance.get(API_ENDPOINTS.UNKNOWN_ALUMNI.GET_STATISTICS);
        if (response.data) {
          setUnknownAlumniStats({
            totalRequests: response.data.totalRequests || 0,
            pending: response.data.pending || 0,
            approved: response.data.approved || 0,
            cancelled: response.data.cancelled || 0,
          });
        }
      } catch (error) {
        console.error("Error fetching unknown alumni statistics:", error);
        // Keep default values (0) on error
      } finally {
        setLoadingUnknownAlumniStats(false);
      }
    };

    fetchUnknownAlumniStatistics();
  }, []);

  return (
    <PageLayout>
      {!isAnyDrawerOpen && (
        <AdminNavBar
          onOpenChat={handleOpenChatDialog}
          onOpenChapterReport={handleOpenChapterReportDialog}
          onOpenEventReport={handleOpenEventReportDialog}
        />
      )}
      <MDBox
        px={3}
        py={3}
        minHeight="calc(100vh - 120px)"
        sx={{
          background: "#f8f9fa",
          pt: { xs: 10, sm: 10, md: 4 },
        }}
      >
        {/* Hero Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            bgcolor: "white",
            mb: 3,
          }}
        >
          <MDTypography variant="h4" fontWeight="bold" color="primary" gutterBottom>
            Admin Dashboard
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary">
            Manage your alumni network, students, and portal access from one central location.
          </MDTypography>
        </Paper>

        {/* Statistics Cards */}
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5}>
                <Card>
                  <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
                    <MDBox
                      variant="gradient"
                      bgColor="primary"
                      color="white"
                      coloredShadow="primary"
                      borderRadius="xl"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="4rem"
                      height="4rem"
                      mt={-3}
                    >
                      <PeopleIcon sx={{ fontSize: 24 }} />
                    </MDBox>
                    <MDBox textAlign="right" lineHeight={1.25}>
                      <MDTypography variant="button" fontWeight="light" color="text">
                        Total Alumni
                      </MDTypography>
                      <MDTypography variant="h4" fontWeight="bold">
                        {loadingStats ? "..." : alumniStats.totalAlumni.toLocaleString()}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                  <Divider />
                  <MDBox pb={2} px={2}>
                    <MDTypography component="p" variant="button" color="text">
                      Total alumni
                    </MDTypography>
                  </MDBox>
                </Card>
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5}>
                <Card>
                  <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
                    <MDBox
                      variant="gradient"
                      bgColor="info"
                      color="white"
                      coloredShadow="info"
                      borderRadius="xl"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="4rem"
                      height="4rem"
                      mt={-3}
                    >
                      <GroupWorkIcon sx={{ fontSize: 24 }} />
                    </MDBox>
                    <MDBox textAlign="right" lineHeight={1.25}>
                      <MDTypography variant="button" fontWeight="light" color="text">
                        Total Chapters
                      </MDTypography>
                      <MDTypography variant="h4" fontWeight="bold">
                        {loadingChapterEventStats ? "..." : chapterEventStats.totalChapters.toLocaleString()}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                  <Divider />
                  <MDBox pb={2} px={2}>
                    <MDTypography component="p" variant="button" color="text">
                      {loadingChapterEventStats ? "" : `${chapterEventStats.activeChapters} active`}
                    </MDTypography>
                  </MDBox>
                </Card>
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5}>
                <Card>
                  <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
                    <MDBox
                      variant="gradient"
                      bgColor="secondary"
                      color="white"
                      coloredShadow="secondary"
                      borderRadius="xl"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="4rem"
                      height="4rem"
                      mt={-3}
                    >
                      <EventIcon sx={{ fontSize: 24 }} />
                    </MDBox>
                    <MDBox textAlign="right" lineHeight={1.25}>
                      <MDTypography variant="button" fontWeight="light" color="text">
                        Total Events
                      </MDTypography>
                      <MDTypography variant="h4" fontWeight="bold">
                        {loadingChapterEventStats ? "..." : chapterEventStats.totalEvents.toLocaleString()}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                  <Divider />
                  <MDBox pb={2} px={2}>
                    <MDTypography component="p" variant="button" color="text">
                      Chapter events
                    </MDTypography>
                  </MDBox>
                </Card>
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5}>
                <Card>
                  <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
                    <MDBox
                      variant="gradient"
                      bgColor="error"
                      color="white"
                      coloredShadow="error"
                      borderRadius="xl"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="4rem"
                      height="4rem"
                      mt={-3}
                    >
                      <PeopleIcon sx={{ fontSize: 24 }} />
                    </MDBox>
                    <MDBox textAlign="right" lineHeight={1.25}>
                      <MDTypography variant="button" fontWeight="light" color="text">
                        Chapter Members
                      </MDTypography>
                      <MDTypography variant="h4" fontWeight="bold">
                        {loadingChapterEventStats ? "..." : chapterEventStats.totalChapterMembers.toLocaleString()}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                  <Divider />
                  <MDBox pb={2} px={2}>
                    <MDTypography component="p" variant="button" color="text">
                      Total members
                    </MDTypography>
                  </MDBox>
                </Card>
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5}>
                <Card>
                  <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
                    <MDBox
                      variant="gradient"
                      bgColor="warning"
                      color="white"
                      coloredShadow="warning"
                      borderRadius="xl"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="4rem"
                      height="4rem"
                      mt={-3}
                    >
                      <TrendingUpIcon sx={{ fontSize: 24 }} />
                    </MDBox>
                    <MDBox textAlign="right" lineHeight={1.25}>
                      <MDTypography variant="button" fontWeight="light" color="text">
                        Event Members
                      </MDTypography>
                      <MDTypography variant="h4" fontWeight="bold">
                        {loadingChapterEventStats ? "..." : chapterEventStats.totalEventMembers.toLocaleString()}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                  <Divider />
                  <MDBox pb={2} px={2}>
                    <MDTypography component="p" variant="button" color="text">
                      Total participants
                    </MDTypography>
                  </MDBox>
                </Card>
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5}>
                <Card>
                  <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
                    <MDBox
                      variant="gradient"
                      bgColor="success"
                      color="white"
                      coloredShadow="success"
                      borderRadius="xl"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="4rem"
                      height="4rem"
                      mt={-3}
                    >
                      <SchoolIcon sx={{ fontSize: 24 }} />
                    </MDBox>
                    <MDBox textAlign="right" lineHeight={1.25}>
                      <MDTypography variant="button" fontWeight="light" color="text">
                        Registered Alumni
                      </MDTypography>
                      <MDTypography variant="h4" fontWeight="bold">
                        {loadingStats ? "..." : alumniStats.registeredAlumni.toLocaleString()}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                  <Divider />
                  <MDBox pb={2} px={2}>
                    <MDTypography component="p" variant="button" color="text">
                      With portal access
                    </MDTypography>
                  </MDBox>
                </Card>
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5}>
                <Card>
                  <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
                    <MDBox
                      variant="gradient"
                      bgColor="warning"
                      color="white"
                      coloredShadow="warning"
                      borderRadius="xl"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="4rem"
                      height="4rem"
                      mt={-3}
                    >
                      <TrendingUpIcon sx={{ fontSize: 24 }} />
                    </MDBox>
                    <MDBox textAlign="right" lineHeight={1.25}>
                      <MDTypography variant="button" fontWeight="light" color="text">
                        Unregistered Alumni
                      </MDTypography>
                      <MDTypography variant="h4" fontWeight="bold">
                        {loadingStats ? "..." : alumniStats.unregisteredAlumni.toLocaleString()}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                  <Divider />
                  <MDBox pb={2} px={2}>
                    <MDTypography component="p" variant="button" color="text">
                      Without portal access
                    </MDTypography>
                  </MDBox>
                </Card>
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5}>
                <Card
                  sx={{
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                  onClick={handleOpenUnknownAlumniDetailsDialog}
                >
                  <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
                    <MDBox
                      variant="gradient"
                      bgColor="info"
                      color="white"
                      coloredShadow="info"
                      borderRadius="xl"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="4rem"
                      height="4rem"
                      mt={-3}
                    >
                      <PersonAddIcon sx={{ fontSize: 24 }} />
                    </MDBox>
                    <MDBox textAlign="right" lineHeight={1.25}>
                      <MDTypography variant="button" fontWeight="light" color="text">
                        Registration Requests
                      </MDTypography>
                      <MDTypography variant="h4" fontWeight="bold">
                        {loadingUnknownAlumniStats ? "..." : unknownAlumniStats.totalRequests.toLocaleString()}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                  <Divider />
                  <MDBox pb={2} px={2}>
                    <MDTypography component="p" variant="button" color="text" display="flex" justifyContent="space-between">
                      <span>Pending: {loadingUnknownAlumniStats ? "..." : unknownAlumniStats.pending}</span>
                      <MDTypography
                        component="span"
                        variant="button"
                        color="primary"
                        sx={{ textDecoration: "underline", cursor: "pointer" }}
                      >
                        View More →
                      </MDTypography>
                    </MDTypography>
                  </MDBox>
                </Card>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        {/* View More Buttons for Analytics */}
        <MDBox mb={3} display="flex" justifyContent="center" gap={2} flexWrap="wrap">
          <MDButton
            variant="contained"
            color="primary"
            size="large"
            onClick={handleOpenAnalyticsDialog}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            View Chapter & Event Analytics
          </MDButton>
          <MDButton
            variant="contained"
            color="info"
            size="large"
            onClick={handleOpenAlumniAnalysisDialog}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Alumni Analysis
          </MDButton>
        </MDBox>

        {/* Quick Actions Section */}
        <MDBox>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: "white",
              mb: 3,
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <DashboardIcon sx={{ fontSize: 28, color: "primary.main", mr: 1.5 }} />
              <MDTypography variant="h5" fontWeight="bold" color="dark">
                Quick Actions
              </MDTypography>
            </Box>
            <MDTypography variant="body2" color="text.secondary">
              Access all administrative functions and manage your alumni network efficiently.
            </MDTypography>
          </Paper>

          <Grid container spacing={3}>
            {cardData.filter(item => item.title !== "Alumni Batch Wise" && item.title !== "All Alumni").map((item, index) => (
              <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
                <Card
                  sx={cardStyle}
                  onClick={
                    item.title === "Chapters"
                      ? handleOpenChapterDrawer
                      : item.title === "Events"
                      ? handleOpenEventDrawer
                      : item.title === "Alumni Rights"
                      ? handleOpenAlumniRightsDrawer
                      : item.title === "Alumni Details"
                      ? handleOpenAlumniDetailsDialog
                      : item.title === "Import Students"
                      ? handleOpenImportStudentDrawer
                      : item.title === "All Alumni"
                      ? handleOpenAlumnusTableDialog
                      : item.title === "Alumni Batch Wise"
                      ? handleOpenAlumniBatchDialog
                      : item.title === "Reports"
                      ? handleOpenReportsDialog
                      : item.title === "All Alumni Details"
                      ? handleOpenAllAlumniDetailsDialog
                      : item.title === "Upload Current Students"
                      ? handleOpenUploadCurrentStudents
                      : item.title === "Send Invitations"
                      ? handleOpenSendInvitations
                      : item.title === "Student Requests"
                      ? handleOpenStudentRequests
                      // Hidden: Import Existing Students
                      // : item.title === "Import Existing Students"
                      // ? handleOpenImportExistingStudents
                      : item.title === "Unknown Users"
                      ? handleOpenUnknownUsers
                      : undefined
                  }
                >
                  <Box>
                    {item.image && !imageErrors[item.title] ? (
                      <Box
                        sx={{
                          width: "100%",
                          height: 120,
                          mb: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          borderRadius: 2,
                          bgcolor: "grey.50",
                        }}
                      >
                        <img
                          src={item.image}
                          alt={item.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                          onError={() => setImageErrors((prev) => ({ ...prev, [item.title]: true }))}
                        />
                      </Box>
                    ) : (
                      <Box sx={getIconWrapperStyle(item.color)}>
                        {item.icon}
                      </Box>
                    )}
                    <MDTypography variant="h6" fontWeight="bold" color="dark" gutterBottom>
                      {item.title}
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary" mb={2} sx={{ minHeight: 40 }}>
                      {item.description}
                    </MDTypography>
                  </Box>
                  <MDButton
                    variant="contained"
                    color={item.color}
                    endIcon={<ArrowForwardIosIcon sx={{ fontSize: 14 }} />}
                    fullWidth
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 500,
                      py: 1,
                    }}
                  >
                    {item.button}
                  </MDButton>
                </Card>
              </Grid>
            ))}
          </Grid>
        </MDBox>
      </MDBox>

      <Drawer
        anchor="right"
        open={chapterDrawerOpen}
        onClose={handleCloseChapterDrawer}
        PaperProps={{ sx: { width: 400 } }}
      >
        <AdminChapterManager onClose={handleCloseChapterDrawer} />
      </Drawer>

      <Drawer
        anchor="right"
        open={eventDrawerOpen}
        onClose={handleCloseEventDrawer}
        PaperProps={{ sx: { width: 400 } }}
      >
        <AdminEventManager onClose={handleCloseEventDrawer} />
      </Drawer>

      <Drawer
        anchor="right"
        open={alumniRightsDrawerOpen}
        onClose={handleCloseAlumniRightsDrawer}
        PaperProps={{ sx: { width: 400 } }}
      >
        <AdminAlumniRightsManager onClose={handleCloseAlumniRightsDrawer} />
      </Drawer>

      <AdminAlumniDetailsDialog
        open={alumniDetailsDialogOpen}
        onClose={handleCloseAlumniDetailsDialog}
      />

      <AdminImportStudentDetails
        open={importStudentDrawerOpen}
        onClose={handleCloseImportStudentDrawer}
      />

      {/* NEW: Full-screen table dialog */}
      <AdminAlumnusTableDialog
        open={alumnusTableDialogOpen}
        onClose={handleCloseAlumnusTableDialog}
      />

      {/* NEW: Alumni Batch Wise dialog */}
      <AdminAlumniBatchWiseDialog
        open={alumniBatchDialogOpen}
        onClose={handleCloseAlumniBatchDialog}
      />

      {/* NEW: Reports dialog */}
      <AdminReportsDialog
        open={reportsDialogOpen}
        onClose={handleCloseReportsDialog}
      />

      {/* NEW: All Alumni Details dialog */}
      <AdminAllAlumniDetailsDialog
        open={allAlumniDetailsDialogOpen}
        onClose={handleCloseAllAlumniDetailsDialog}
      />

      {/* Current Students Management */}
      <AdminUploadCurrentStudents
        open={uploadCurrentStudentsOpen}
        onClose={handleCloseUploadCurrentStudents}
      />

      <AdminSendStudentInvitations
        open={sendInvitationsOpen}
        onClose={handleCloseSendInvitations}
      />

      <AdminStudentRequestsManager
        open={studentRequestsOpen}
        onClose={handleCloseStudentRequests}
      />

      {/* Hidden: Import Existing Students */}
      {/* <AdminImportExistingStudents
        open={importExistingStudentsOpen}
        onClose={handleCloseImportExistingStudents}
      /> */}

      {/* Unknown Users Management */}
      <AdminUnknownUsersManager
        open={unknownUsersOpen}
        onClose={handleCloseUnknownUsers}
      />

      {/* Chapter & Event Analytics Dialog */}
      <AdminChapterEventAnalyticsDialog
        open={analyticsDialogOpen}
        onClose={handleCloseAnalyticsDialog}
      />

      {/* Alumni Analysis Dialog */}
      <AdminAlumniAnalysisDialog
        open={alumniAnalysisDialogOpen}
        onClose={handleCloseAlumniAnalysisDialog}
      />

      {/* Unknown Alumni Registration Details Dialog */}
      <UnknownAlumniRegistrationDetailsDialog
        open={unknownAlumniDetailsDialogOpen}
        onClose={handleCloseUnknownAlumniDetailsDialog}
      />

      {/* Global Chat Dialog */}
      <AdminGlobalChatDialog
        open={chatDialogOpen}
        onClose={handleCloseChatDialog}
      />

      {/* Chapter Report Dialog */}
      <AdminChapterReportDialog
        open={chapterReportDialogOpen}
        onClose={handleCloseChapterReportDialog}
        showAllChapters={showAllChapters}
      />

      {/* Event Report Dialog */}
      <AdminEventReportDialog
        open={eventReportDialogOpen}
        onClose={handleCloseEventReportDialog}
        showAllEvents={showAllEvents}
      />
    </PageLayout>
  );
};

export default AdminHome;
